import {
  collection, doc, addDoc, setDoc, getDoc, getDocs, getDocsFromCache, writeBatch,
  updateDoc, deleteDoc, query, where, orderBy, limit, Timestamp, deleteField,
  QuerySnapshot, QueryDocumentSnapshot, DocumentData,
} from 'firebase/firestore';
import { db } from './config';
import { Project, EbtronResult, PowerResult, DaltResult, FacilityAssessment, FacilityAsset, SystemKey, Organization, UserProfile, PendingInvite, OrgRole, BoilerECMResult, MembershipEvent } from '../models/types';
import { calculateSystemData, calculateOverallScore, getBuildingRating, maxPossibleScore } from '../modules/facility/facilityCalculations';
// Cache-first read helpers live in utils so pmScheduler.ts / compliance.ts share them.
import { withTimeout, queryCacheFirst, docCacheFirst, retry, stripUndefined } from './utils';

// ─── Projects ──────────────────────────────────────────────────────────────

const PROJECTS = 'projects';

function mapProject(id: string, data: Record<string, any>): Project {
  return {
    id,
    userId: data.userId ?? '',
    orgId: data.orgId ?? undefined,  // fix: was missing, broke org-member report access
    name: data.name ?? 'Untitled Project',
    address: data.address ?? '',
    client: data.client ?? '',
    sitePhotoUrl: data.sitePhotoUrl ?? undefined,
    description: data.description ?? '',
    status: data.status ?? 'active',
    assignedContractors: data.assignedContractors ?? [],
    createdAt: data.createdAt?.toDate?.()?.toISOString() ?? data.createdAt ?? '',
    updatedAt: data.updatedAt?.toDate?.()?.toISOString() ?? data.updatedAt ?? '',
  };
}

export const createProject = async (userId: string, name: string, address?: string, description?: string, orgId?: string, client?: string): Promise<string> => {
  const now = Timestamp.now();
  const ref = await withTimeout(addDoc(collection(db, PROJECTS), {
    userId, orgId: orgId ?? null, name, address: address ?? '', client: client ?? '', description: description ?? '',
    status: 'active', createdAt: now, updatedAt: now,
  }));
  return ref.id;
};

const snapToProjects = (snap: QuerySnapshot<DocumentData>): Project[] =>
  snap.docs.map(d => mapProject(d.id, d.data())).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

/** Cache-first. `onRefresh` (optional) gets the fresh server copy in the background. */
export const getProject = (id: string, onRefresh?: (p: Project | null) => void): Promise<Project | null> =>
  docCacheFirst(doc(db, PROJECTS, id), s => (s.exists() ? mapProject(s.id, s.data()) : null), onRefresh);

/* Personal projects only (orgId null, as createProject writes it). A user with no org reaches
   just these: the rules no longer let a creator into an org project they've left, and a query
   that could match one would be refused outright. */
export const getUserProjects = (userId: string, onRefresh?: (p: Project[]) => void): Promise<Project[]> =>
  queryCacheFirst(query(collection(db, PROJECTS), where('userId', '==', userId), where('orgId', '==', null)), snapToProjects, onRefresh);

export const updateProject = async (id: string, partial: Partial<Omit<Project, 'id' | 'userId' | 'createdAt'>>): Promise<void> => {
  await withTimeout(updateDoc(doc(db, PROJECTS, id), { ...partial, updatedAt: Timestamp.now() }));
};

/* Separate from updateProject: Firestore's JS SDK rejects `undefined` field values outright, so
   clearing sitePhotoUrl needs the deleteField() sentinel rather than a plain Partial<Project>. */
export const removeProjectSitePhoto = async (id: string): Promise<void> => {
  await withTimeout(updateDoc(doc(db, PROJECTS, id), { sitePhotoUrl: deleteField(), updatedAt: Timestamp.now() }));
};

export const deleteProject = async (id: string): Promise<void> => {
  await withTimeout(deleteDoc(doc(db, PROJECTS, id)));
};

export const getOrgProjects = (orgId: string, onRefresh?: (p: Project[]) => void): Promise<Project[]> =>
  queryCacheFirst(query(collection(db, PROJECTS), where('orgId', '==', orgId)), snapToProjects, onRefresh);

/* Projects a contractor is assigned to. Contractors are org members but the security rules deny
   them the org-wide query above, so their dashboard queries by assignment instead. */
export const getAssignedProjects = (uid: string, onRefresh?: (p: Project[]) => void): Promise<Project[]> =>
  queryCacheFirst(query(collection(db, PROJECTS), where('assignedContractors', 'array-contains', uid)), snapToProjects, onRefresh);

// ─── Organizations ────────────────────────────────────────────────────

const ORGS = 'organizations';

/* Org creation moved to the createOrganization callable — see firebase/membership.ts. It has
   to write the caller's admin membership in the same batch, and users/{uid}.orgId/role are no
   longer client-writable. */

export const getOrganization = async (orgId: string): Promise<Organization | null> => {
  const snap = await withTimeout(getDoc(doc(db, ORGS, orgId)));
  if (!snap.exists()) return null;
  const d = snap.data();
  return {
    id: snap.id, name: d.name, adminUid: d.adminUid,
    createdAt: d.createdAt?.toDate?.()?.toISOString() ?? d.createdAt ?? '',
    enabledModules: d.enabledModules ?? undefined,
  };
};

export const updateOrganization = async (orgId: string, name: string): Promise<void> => {
  await withTimeout(updateDoc(doc(db, ORGS, orgId), { name }));
};

export const updateOrgEnabledModules = async (orgId: string, enabledModules: string[]): Promise<void> => {
  await withTimeout(updateDoc(doc(db, ORGS, orgId), { enabledModules }));
};

// ─── User Profiles ───────────────────────────────────────────────────

const USERS = 'users';

// Always use UID as the document ID so lookups are O(1) and never ambiguous
export const setUserProfile = async (uid: string, profile: Partial<Omit<UserProfile, 'uid'>>): Promise<void> => {
  const ref = doc(db, USERS, uid);
  const snap = await withTimeout(getDoc(ref));
  if (snap.exists()) {
    await withTimeout(updateDoc(ref, profile));
  } else {
    // setDoc with merge:true creates the doc with the UID as its ID
    await withTimeout(setDoc(ref, { uid, ...profile, joinedAt: Timestamp.now() }, { merge: true }));
  }
};

export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  const snap = await withTimeout(getDoc(doc(db, USERS, uid)));
  if (!snap.exists()) return null;
  const d = snap.data();
  return {
    uid,
    email: d.email ?? '',
    displayName: d.displayName ?? '',
    orgId: d.orgId ?? undefined,
    role: d.role ?? undefined,
    joinedAt: d.joinedAt?.toDate?.()?.toISOString() ?? d.joinedAt ?? '',
    disabled: d.disabled ?? false,
  };
};

export const getOrgMembers = async (orgId: string): Promise<UserProfile[]> => {
  const q = query(collection(db, USERS), where('orgId', '==', orgId));
  const snap = await withTimeout(getDocs(q));
  return snap.docs.map(d => ({
    uid: d.id,
    email: d.data().email ?? '',
    displayName: d.data().displayName ?? '',
    orgId: d.data().orgId,
    role: d.data().role,
    joinedAt: d.data().joinedAt?.toDate?.()?.toISOString() ?? d.data().joinedAt ?? '',
    disabled: d.data().disabled ?? false,
  }));
};

export const removeOrgMember = async (uid: string): Promise<void> => {
  // Use deleteField() to fully remove orgId/role rather than setting null,
  // which can interfere with Firestore queries using where('orgId', '==', x)
  await withTimeout(updateDoc(doc(db, USERS, uid), {
    orgId: deleteField(),
    role: deleteField(),
  }));
};

// ─── Pending Invites ──────────────────────────────────────────────────

const INVITES = 'pendingInvites';

// Keep in step with INVITE_TTL_MS in functions/src/index.ts, which enforces it on acceptance.
export const INVITE_TTL_DAYS = 14;

/* One invite per org + address: the id is `{orgId}_{email}` (the rules require it), so inviting
   the same address again — or pressing Resend — overwrites the invite and restarts its expiry
   rather than leaving duplicates for acceptInvite to choose between. */
export const createInvite = async (email: string, orgId: string, role: OrgRole, invitedBy: string): Promise<string> => {
  const address = email.trim().toLowerCase();
  const id = `${orgId}_${address}`;
  const now = Timestamp.now();
  await withTimeout(setDoc(doc(db, INVITES, id), {
    email: address, orgId, role, invitedBy, createdAt: now,
    expiresAt: Timestamp.fromMillis(now.toMillis() + INVITE_TTL_DAYS * 24 * 60 * 60 * 1000),
  }));
  return id;
};

/* Looking up your own invite is the acceptInvite callable's job now: it matches on the auth
   token's email rather than a client-supplied one, and applies the membership itself. */

export const getOrgInvites = async (orgId: string): Promise<PendingInvite[]> => {
  const q = query(collection(db, INVITES), where('orgId', '==', orgId));
  const snap = await withTimeout(getDocs(q));
  return snap.docs.map(d => ({ id: d.id, email: d.data().email, orgId: d.data().orgId,
    role: d.data().role, createdAt: d.data().createdAt?.toDate?.()?.toISOString() ?? '',
    expiresAt: d.data().expiresAt?.toDate?.()?.toISOString() }))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
};

/** Most recent membership activity for the org (admins only — see firestore.rules). */
export const getMembershipEvents = async (orgId: string, max = 50): Promise<MembershipEvent[]> => {
  const q = query(collection(db, ORGS, orgId, 'membershipEvents'), orderBy('at', 'desc'), limit(max));
  const snap = await withTimeout(getDocs(q));
  return snap.docs.map(d => {
    const data = d.data();
    return { ...data, id: d.id, at: data.at?.toDate?.()?.toISOString() ?? '' } as MembershipEvent;
  });
};

export const deleteInvite = async (id: string): Promise<void> => {
  await withTimeout(deleteDoc(doc(db, INVITES, id)));
};

// ─── Boiler ECM Results ───────────────────────────────────────────────────

const boilerECMCol = (projectId: string) => collection(db, PROJECTS, projectId, 'boilerECMResults');

export const saveBoilerECMResult = async (result: Omit<BoilerECMResult, 'id'>): Promise<string> => {
  // fix: strip undefined fields — Firestore rejects them (same pattern as savePowerResult/saveEbtronResult)
  const clean = Object.fromEntries(
    Object.entries({ ...result, createdAt: Timestamp.now() }).filter(([, v]) => v !== undefined)
  );
  const ref = await withTimeout(addDoc(boilerECMCol(result.projectId), clean));
  return ref.id;
};

export const getBoilerECMResults = (projectId: string, onRefresh?: (r: BoilerECMResult[]) => void): Promise<BoilerECMResult[]> =>
  queryCacheFirst(boilerECMCol(projectId), snap =>
    snap.docs
      .map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          createdAt: data.createdAt?.toDate?.()?.toISOString() ?? data.createdAt ?? '',
        } as BoilerECMResult;
      })
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt)), onRefresh);

export const deleteBoilerECMResult = async (projectId: string, id: string): Promise<void> => {
  await withTimeout(deleteDoc(doc(db, PROJECTS, projectId, 'boilerECMResults', id)));
};

// ─── Ebtron Results ────────────────────────────────────────────────────────

const ebtronCol = (projectId: string) => collection(db, PROJECTS, projectId, 'ebtronResults');

function mapEbtronResult(id: string, data: Record<string, any>): EbtronResult {
  return {
    id, projectId: data.projectId ?? '',
    sensorLabel: data.sensorLabel ?? '',
    minDisplayed: data.minDisplayed ?? 0, minActual: data.minActual ?? 0,
    maxDisplayed: data.maxDisplayed ?? 0, maxActual: data.maxActual ?? 0,
    ductArea: data.ductArea ?? 0, gain: data.gain ?? 0,
    offsetCfm: data.offsetCfm ?? 0, offsetFpm: data.offsetFpm ?? 0,
    midrangeDisplayed: data.midrangeDisplayed, midrangeActual: data.midrangeActual,
    midrangeAccuracyPct: data.midrangeAccuracyPct, midrangePass: data.midrangePass,
    notes: data.notes ?? '',
    createdAt: data.createdAt?.toDate?.()?.toISOString() ?? data.createdAt ?? '',
  };
}

export const saveEbtronResult = async (result: Omit<EbtronResult, 'id'>): Promise<string> => {
  const clean = Object.fromEntries(
    Object.entries({ ...result, createdAt: Timestamp.now() }).filter(([, v]) => v !== undefined)
  );
  const ref = await withTimeout(addDoc(ebtronCol(result.projectId), clean));
  return ref.id;
};

export const getEbtronResults = (projectId: string, onRefresh?: (r: EbtronResult[]) => void): Promise<EbtronResult[]> =>
  queryCacheFirst(ebtronCol(projectId), snap =>
    snap.docs.map(d => mapEbtronResult(d.id, d.data())).sort((a, b) => b.createdAt.localeCompare(a.createdAt)), onRefresh);

export const deleteEbtronResult = async (projectId: string, id: string): Promise<void> => {
  await withTimeout(deleteDoc(doc(db, PROJECTS, projectId, 'ebtronResults', id)));
};

// ─── Power Results ─────────────────────────────────────────────────────────

const powerCol = (projectId: string) => collection(db, PROJECTS, projectId, 'powerResults');

function mapPowerResult(id: string, data: Record<string, any>): PowerResult {
  return {
    id, projectId: data.projectId ?? '', loadType: data.loadType ?? 'three-phase-inductive',
    mode: data.mode ?? 'amperage', kw: data.kw, voltage: data.voltage, amperage: data.amperage,
    powerFactor: data.powerFactor, efficiency: data.efficiency,
    resultAmperage: data.resultAmperage, resultVoltage: data.resultVoltage, resultKw: data.resultKw,
    notes: data.notes ?? '',
    createdAt: data.createdAt?.toDate?.()?.toISOString() ?? data.createdAt ?? '',
  };
}

export const savePowerResult = async (result: Omit<PowerResult, 'id'>): Promise<string> => {
  // Firestore rejects undefined values — strip them out before writing
  const clean = Object.fromEntries(
    Object.entries({ ...result, createdAt: Timestamp.now() }).filter(([, v]) => v !== undefined)
  );
  const ref = await withTimeout(addDoc(powerCol(result.projectId), clean));
  return ref.id;
};

export const getPowerResults = (projectId: string, onRefresh?: (r: PowerResult[]) => void): Promise<PowerResult[]> =>
  queryCacheFirst(powerCol(projectId), snap =>
    snap.docs.map(d => mapPowerResult(d.id, d.data())).sort((a, b) => b.createdAt.localeCompare(a.createdAt)), onRefresh);

export const deletePowerResult = async (projectId: string, id: string): Promise<void> => {
  await withTimeout(deleteDoc(doc(db, PROJECTS, projectId, 'powerResults', id)));
};

// ─── DALT Results ──────────────────────────────────────────────────────────

const daltCol = (projectId: string) => collection(db, PROJECTS, projectId, 'daltResults');

function mapDaltResult(id: string, data: Record<string, any>): DaltResult {
  return {
    id, projectId: data.projectId ?? '',
    sectionLabel: data.sectionLabel ?? '',
    testParameters: data.testParameters ?? { testPressure: 0, leakageClass: 0, pressureDrop: 0, orificeSize: '', testDate: '', witnessedBy: '', ductSectionId: '' },
    rectangularDucts: data.rectangularDucts ?? [], roundDucts: data.roundDucts ?? [],
    ovalDucts: data.ovalDucts ?? [], transitionDucts: data.transitionDucts ?? [],
    radialElbows: data.radialElbows ?? [], rectangularElbows: data.rectangularElbows ?? [],
    roundElbows: data.roundElbows ?? [], testResults: data.testResults ?? null,
    notes: data.notes ?? '',
    createdAt: data.createdAt?.toDate?.()?.toISOString() ?? data.createdAt ?? '',
  };
}

export const saveDaltResult = async (result: Omit<DaltResult, 'id'>): Promise<string> => {
  const ref = await withTimeout(addDoc(daltCol(result.projectId), { ...result, createdAt: Timestamp.now() }));
  return ref.id;
};

export const getDaltResults = (projectId: string, onRefresh?: (r: DaltResult[]) => void): Promise<DaltResult[]> =>
  queryCacheFirst(daltCol(projectId), snap =>
    snap.docs.map(d => mapDaltResult(d.id, d.data())).sort((a, b) => b.createdAt.localeCompare(a.createdAt)), onRefresh);

export const deleteDaltResult = async (projectId: string, id: string): Promise<void> => {
  await withTimeout(deleteDoc(doc(db, PROJECTS, projectId, 'daltResults', id)));
};

// ─── Facility Assessments ──────────────────────────────────────────────────

const facilityCol = (projectId: string) => collection(db, PROJECTS, projectId, 'facilityAssessments');

/* Synchronous by design: the id is minted client-side and setDoc lands in the local cache before
   this returns, so the caller can show the new assessment immediately — offline included. `ack` is
   the server acknowledgement; offline it stays pending until reconnect, so it is deliberately NOT
   raced against withTimeout (the old addDoc-plus-timeout made every offline create fail after 20 s
   even though the document was safely queued, and the caller could not show it until a reload).
   Only a genuine rejection (rules, invalid data) should be treated as an error. Assets are never
   written here — a brand-new assessment has none yet; see saveFacilityAsset. */
export const saveFacilityAssessment = (a: Omit<FacilityAssessment, 'id'>): { id: string; ack: Promise<void> } => {
  const ref = doc(facilityCol(a.projectId));
  const ack = setDoc(ref, { ...a, createdAt: Timestamp.now(), updatedAt: Timestamp.now() });
  return { id: ref.id, ack };
};

/* Assessment-level fields only (building/contact/assessor info, plans, notes) — NOT the four system
   asset arrays. Those live one level down, one Firestore document per asset (see saveFacilityAsset /
   updateFacilityAsset), specifically so two people editing two different assets can never overwrite
   each other: this whole-document update used to carry every asset in the building on every edit,
   so whichever save landed second clobbered every asset edit made by anyone else since they'd
   loaded the page — the "one device per building" limitation. Deliberately NOT wrapped in
   withTimeout: with persistentLocalCache the write lands in the local cache at once and this
   promise is the *server ack*, which offline never arrives — racing it against a 20 s timer only
   manufactured a "failure" for a write that was already durably queued. The facility module owns
   the timing and shows "queued / will sync"; it awaits this only to flip the status to Saved. */
export const updateFacilityAssessment = async (projectId: string, id: string, partial: Partial<FacilityAssessment>): Promise<void> => {
  const { controls, electrical, mechanical, plumbing, overallScore, overallRating, ...assessmentLevel } = partial;
  const clean = stripUndefined({ ...assessmentLevel, updatedAt: Timestamp.now() });
  await updateDoc(doc(db, PROJECTS, projectId, 'facilityAssessments', id), clean as any);
};

const facilityAssetsCol = (projectId: string, assessmentId: string) =>
  collection(db, PROJECTS, projectId, 'facilityAssessments', assessmentId, 'assets');

type StoredAsset = FacilityAsset & { systemKey: SystemKey; order: number };

/* Strips the fields recomputed on every read (score/rating — see calculateSystemData) and any
   undefined, so a clear (e.g. visualInspection reset to null, which IS meaningful and kept) never
   trips Firestore's "no undefined" rule. `order` is omitted (stripped as undefined) on an update —
   an existing asset's position was set once, at creation, and an edit must never reshuffle it. */
const assetWritePayload = (asset: FacilityAsset, systemKey: SystemKey, order?: number) => {
  const { id, score, rating, ...rest } = asset;
  return stripUndefined({ ...rest, systemKey, order, updatedAt: Timestamp.now() });
};

/* One document per asset, so an edit to Asset A can never be lost to a concurrent edit of Asset B —
   see updateFacilityAssessment. `order` preserves the "added in this order" display the old array
   gave for free; Date.now() is monotonic enough for clicks/imports, and ties only matter for
   display order, never correctness. */
export const saveFacilityAsset = async (
  projectId: string, assessmentId: string, systemKey: SystemKey, asset: FacilityAsset, order = Date.now(),
): Promise<void> => {
  await setDoc(doc(facilityAssetsCol(projectId, assessmentId), asset.id), assetWritePayload(asset, systemKey, order));
};

/* updateDoc merges: a key that is simply absent from the payload keeps its server value. The old
   whole-array write replaced the asset outright, so removing a pin "just worked"; now the removed
   optional key has to be deleted explicitly or the pin reappears on the next load. `pin` is the
   only top-level optional the UI removes (photos is always an array; criterionNotes is rewritten
   as a whole map). */
export const updateFacilityAsset = async (
  projectId: string, assessmentId: string, assetId: string, asset: FacilityAsset, systemKey: SystemKey,
): Promise<void> => {
  const payload = assetWritePayload(asset, systemKey);
  if (asset.pin === undefined) payload.pin = deleteField();
  await updateDoc(doc(facilityAssetsCol(projectId, assessmentId), assetId), payload as any);
};

export const deleteFacilityAsset = async (projectId: string, assessmentId: string, assetId: string): Promise<void> => {
  await deleteDoc(doc(facilityAssetsCol(projectId, assessmentId), assetId));
};

/* Bulk create only (COBie import) — every entry is a brand-new asset doc, so there is no
   read-modify-write race to worry about; one batch keeps it atomic. */
export const batchSaveFacilityAssets = async (
  projectId: string, assessmentId: string, entries: { systemKey: SystemKey; asset: FacilityAsset }[],
): Promise<void> => {
  const batch = writeBatch(db);
  entries.forEach(({ systemKey, asset }, i) => {
    batch.set(doc(facilityAssetsCol(projectId, assessmentId), asset.id), assetWritePayload(asset, systemKey, Date.now() + i));
  });
  await batch.commit();
};

const snapToAsset = (d: QueryDocumentSnapshot<DocumentData>): StoredAsset => {
  const data = d.data();
  return {
    id: d.id,
    assetId: data.assetId ?? '',
    description: data.description ?? '',
    location: data.location ?? '',
    manufacturer: data.manufacturer ?? '',
    model: data.model ?? '',
    serialNumber: data.serialNumber ?? '',
    age: data.age ?? null,
    remainingServiceLife: data.remainingServiceLife ?? null,
    visualInspection: data.visualInspection ?? null,
    visualIssues: data.visualIssues,
    operationalTest: data.operationalTest ?? null,
    operationalIssues: data.operationalIssues,
    maintenanceRecords: data.maintenanceRecords ?? null,
    maintenanceServiceLevel: data.maintenanceServiceLevel ?? null,
    criterionNotes: data.criterionNotes,
    notes: data.notes ?? '',
    photos: data.photos ?? [],
    pin: data.pin,
    // Legacy field (pre-description records); migrateAssessment folds it into description on load.
    ...(data.classification !== undefined ? { classification: data.classification } : {}),
    systemKey: (data.systemKey ?? 'mechanical') as SystemKey,
    order: data.order ?? 0,
  };
};

/* Cache-first, same rationale as queryCacheFirst in utils.ts (not reused for the outer read:
   assembling an assessment needs an extra per-document async fetch of its assets subcollection,
   which that helper's synchronous map callback can't do). Reads the assessment docs, then each
   one's assets subcollection, and reassembles the same nested-array FacilityAssessment shape every
   other module (report, CSV/COBie export, FacilityModule) already expects — so this migration is
   invisible to everything except firestore.ts and the facility module's save path.

   The assets read has to be cache-first on the instant-paint path too: a server-first getDocs per
   assessment here re-introduced the weak-signal navigation stall (up to retry × withTimeout before
   the SDK gives up and serves cache) that queryCacheFirst was written to remove. Only the
   background refresh and the cold (nothing cached) path go to the server for assets. */
const cachedAssets = (col: ReturnType<typeof facilityAssetsCol>): Promise<StoredAsset[]> =>
  queryCacheFirst(col, snap => snap.docs.map(snapToAsset));
const serverAssets = async (col: ReturnType<typeof facilityAssetsCol>): Promise<StoredAsset[]> =>
  (await retry(() => withTimeout(getDocs(col)))).docs.map(snapToAsset);

export const getFacilityAssessments = async (
  projectId: string, onRefresh?: (a: FacilityAssessment[]) => void,
): Promise<FacilityAssessment[]> => {
  const assemble = async (
    snap: QuerySnapshot<DocumentData>, readAssets: typeof cachedAssets,
  ): Promise<FacilityAssessment[]> =>
    Promise.all(snap.docs.map(async d =>
      assembleAssessment(d.id, d.data(), projectId, await readAssets(facilityAssetsCol(projectId, d.id))),
    )).then(list => list.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));

  try {
    const cached = await getDocsFromCache(facilityCol(projectId));
    if (!cached.empty) {
      const result = await assemble(cached, cachedAssets);
      if (onRefresh) {
        getDocs(facilityCol(projectId)).then(s => assemble(s, serverAssets)).then(onRefresh)
          .catch(() => { /* stay on cached */ });
      }
      return result;
    }
  } catch { /* nothing usable in cache — fall through to the server */ }
  const server = await retry(() => withTimeout(getDocs(facilityCol(projectId))));
  return assemble(server, serverAssets);
};

function assembleAssessment(
  id: string, data: Record<string, any>, projectId: string, assets: StoredAsset[],
): FacilityAssessment {
  const bySystem = (key: SystemKey) => assets
    .filter(a => a.systemKey === key)
    .sort((a, b) => a.order - b.order)
    .map(({ systemKey, order, ...asset }) => asset as FacilityAsset);
  /* Recompute scores from the stored assets on read, so a report or the module never shows a score
     written under earlier scoring rules (the nullable-condition changes, say). calculateSystemData
     attaches fresh per-asset and system score/rating; overall follows from the four systems. This
     is the single load path both the module and the reports use. */
  const controls = calculateSystemData(bySystem('controls'));
  const electrical = calculateSystemData(bySystem('electrical'));
  const mechanical = calculateSystemData(bySystem('mechanical'));
  const plumbing = calculateSystemData(bySystem('plumbing'));
  const forScore = { controls, electrical, mechanical, plumbing } as FacilityAssessment;
  const overallScore = calculateOverallScore(forScore);
  const overallRating = getBuildingRating(overallScore, maxPossibleScore(forScore));
  return {
    id,
    projectId: data.projectId ?? projectId,
    buildingName: data.buildingName ?? '',
    owner: data.owner ?? '',
    street: data.street ?? '',
    cityStateZip: data.cityStateZip ?? '',
    contactName: data.contactName ?? '',
    contactPhone: data.contactPhone ?? '',
    contactCell: data.contactCell ?? '',
    contactEmail: data.contactEmail ?? '',
    alt1Name: data.alt1Name ?? '',
    alt1Phone: data.alt1Phone ?? '',
    alt1Cell: data.alt1Cell ?? '',
    alt1Email: data.alt1Email ?? '',
    alt2Name: data.alt2Name ?? '',
    alt2Phone: data.alt2Phone ?? '',
    alt2Cell: data.alt2Cell ?? '',
    alt2Email: data.alt2Email ?? '',
    assessorName: data.assessorName ?? '',
    assessorPhone: data.assessorPhone ?? '',
    assessorCell: data.assessorCell ?? '',
    assessorEmail: data.assessorEmail ?? '',
    assessmentDate: data.assessmentDate ?? '',
    buildingType: data.buildingType ?? '',
    squareFootage: data.squareFootage ?? '',
    plans: data.plans ?? [],
    controls, electrical, mechanical, plumbing,
    overallScore, overallRating,
    notes: data.notes ?? '',
    createdAt: data.createdAt?.toDate?.()?.toISOString() ?? data.createdAt ?? '',
    updatedAt: data.updatedAt?.toDate?.()?.toISOString() ?? data.updatedAt ?? '',
  } as FacilityAssessment;
}

/* Firestore does not cascade-delete subcollections — the assets under this assessment must be
   dropped explicitly, or they'd sit orphaned (unreadable through the app, still billed, still
   returned by a raw collectionGroup query). Storage cleanup for their photos stays the facility
   module's job (it already has the paths from the in-memory copy before this call). */
export const deleteFacilityAssessment = async (projectId: string, id: string): Promise<void> => {
  // Parent first: if this times out the building simply survives intact. Assets-first and a
  // failed parent delete would leave a building that looks like it lost every asset.
  await withTimeout(deleteDoc(doc(db, PROJECTS, projectId, 'facilityAssessments', id)));
  const assetsSnap = await withTimeout(getDocs(facilityAssetsCol(projectId, id)));
  const docs = assetsSnap.docs;
  for (let i = 0; i < docs.length; i += 450) {   // a batch is capped at 500 writes
    const batch = writeBatch(db);
    docs.slice(i, i + 450).forEach(d => batch.delete(d.ref));
    await batch.commit();
  }
};
